import React, { useCallback, useMemo } from 'react'
import { Grid, makeStyles } from '@material-ui/core'
import DazzedParagraph16 from 'components/common/text/DazzedParagraph16'
import DazzedParagraph12 from 'components/common/text/DazzedParagraph12'
import SelectInput from 'components/common/SelectInput'
import TriggerToActionArrow from 'components/common/icons/TriggerToActionArrow'
import { useTranslation } from 'react-i18next'
import { TriggerTypes, TriggerConfigs, actionTypes, canSelectNotifyPractice } from 'consts/automatedActionConsts'
import { Message, PatientTag, AutomationsAssignPatient } from 'components/common/icons'
import NeedsAttention from 'components/common/icons/NeedsAttention'
import useRolePermissions from 'hooks/useRolePermissions'
import usePlanLimits from 'hooks/usePlanLimits'
import moment from 'moment'

const useStyles = makeStyles({
  label: {
    color: 'var(--text-color-11)'
  },
  dropdown: {
    maxHeight: '350px !important'
  },
  hints: {
    // the same gap the trigger column's spacing={1} put between the dropdown and the hints
    marginTop: 8
  }
})

// The picker stores 24h 'HH:mm'; the design writes the hour as '10:00am'.
const formatExecutionHour = hour => moment(hour, 'HH:mm').format('h:mma')

// The dropdown can be terse because the hints under it explain the trigger; the rules list has no
// hints, so it keeps the full `triggerTypes` label.
const SHORT_OPTION_LABEL_KEYS = {
  [TriggerTypes.PatientEveryXWeeks]:
    'pages.accountSettings.automations.editorModal.triggerTypeOptionLabels.patientEveryXWeeks'
}

const RuleEditor = ({
  triggerType,
  setTriggerType,
  actionType,
  setActionType,
  savedActionType,
  isSystem,
  setConditions,
  errors,
  executionHour
}) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { permissions } = useRolePermissions()
  const { isHI } = usePlanLimits()
  // Every trigger the service can execute is offered. patientEveryXWeeks was gated behind
  // timeBasedRecurringAutomations while the backend that provisions its EventBridge schedule was
  // not yet deployed; that landed in grin-automated-actions-service #79, so the gate is gone. It
  // was also never reachable in practice: no registry entry was ever added for the flag, so it
  // could only be set by writing to a user's featureFlags directly - 1 staging record had it, 0 in
  // production.
  const triggerTypesOptions = useMemo(
    () =>
      Object.values(TriggerTypes).map(triggerTypeValue => ({
        value: triggerTypeValue,
        label: t(
          SHORT_OPTION_LABEL_KEYS[triggerTypeValue] ||
            `pages.accountSettings.automations.triggerTypes.${triggerTypeValue}`
        )
      })),
    [t]
  )

  const actionTypesOptions = useMemo(
    () =>
      [
        {
          value: actionTypes.SendMessage,
          label: t(`pages.accountSettings.automations.actionTypes.${actionTypes.SendMessage}`),
          icon: (
            <div style={{ marginTop: 6 }}>
              <Message />
            </div>
          ),
          condition: permissions.sendChatMessages && triggerType !== TriggerTypes.LeadCreated
        },
        {
          value: actionTypes.AddPatientTag,
          label: t(`pages.accountSettings.automations.actionTypes.${actionTypes.AddPatientTag}`),
          icon: (
            <div style={{ marginTop: 6 }}>
              <PatientTag />
            </div>
          ),
          condition: true
        },
        {
          value: actionTypes.AssignPatient,
          label: t(`pages.accountSettings.automations.actionTypes.${actionTypes.AssignPatient}`),
          icon: (
            <div style={{ marginTop: 6 }}>
              <AutomationsAssignPatient />
            </div>
          ),
          condition: true
        },
        {
          value: actionTypes.ShowAnnouncement,
          label: t(`pages.accountSettings.automations.actionTypes.${actionTypes.ShowAnnouncement}`),
          icon: <></>,
          condition: permissions.opsDashboardSystemAutomations
        },
        {
          value: actionTypes.NotifyPractice,
          label: t(`pages.accountSettings.automations.actionTypes.${actionTypes.NotifyPractice}`),
          icon: (
            <div style={{ marginTop: 6 }}>
              {/* Sized by ink rather than box: this glyph fills its viewBox edge to edge, while
                  Message ships 16px of ink in a 24px box and the other two ~23px. 20 sits it
                  between them instead of rendering as a small dot. */}
              <NeedsAttention width={20} height={20} />
            </div>
          ),
          // Available to practices on an HI plan, not just system automations — the resulting
          // Needs Attention is flagged with the `automation` reason so its origin stays visible
          // on the patient. savedActionType, not actionType: see canSelectNotifyPractice.
          condition: canSelectNotifyPractice({
            isHiPlan: isHI,
            isSystemRule: isSystem,
            savedActionType
          })
        }
      ].filter(({ condition }) => condition),
    [t, permissions, triggerType, isHI, isSystem, savedActionType]
  )

  const handleTriggerTypeChange = useCallback(
    newTriggerType => {
      setConditions([])
      setTriggerType(newTriggerType)
    },
    [setConditions, setTriggerType]
  )

  return (
    <div>
      <Grid container alignItems="center">
        <Grid item xs={5}>
          <Grid container direction="column" spacing={1}>
            <Grid item>
              <DazzedParagraph16 bold className={classes.label}>
                {t('pages.accountSettings.automations.editorModal.triggerTypeLabel')}
              </DazzedParagraph16>
            </Grid>
            <Grid item>
              <SelectInput
                style={{ bright: true, thick: true }}
                options={triggerTypesOptions}
                value={triggerType}
                onChange={option => handleTriggerTypeChange(option.value)}
                placeholder={t('pages.accountSettings.automations.editorModal.triggerTypePlaceholder')}
                dropdownClassName={classes.dropdown}
                errorMessage={errors.requiredFields?.triggerType}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <TriggerToActionArrow style={{ marginTop: 15 }} />
        </Grid>
        <Grid item xs={5}>
          <Grid container direction="column" spacing={1}>
            <Grid item>
              <DazzedParagraph16 bold className={classes.label}>
                {t('pages.accountSettings.automations.editorModal.actionTypeLabel')}
              </DazzedParagraph16>
            </Grid>
            <Grid item>
              <SelectInput
                style={{ bright: true, thick: true }}
                options={actionTypesOptions}
                value={actionType}
                onChange={option => setActionType(option.value)}
                placeholder={t('pages.accountSettings.automations.editorModal.actionPlaceholder')}
                dropdownClassName={classes.dropdown}
                errorMessage={errors.requiredFields?.actionType}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {/* The design puts both lines under the trigger rather than beside the fields they
          describe, so the practice reads when the clock starts and when it fires before picking
          anything. They sit in their own row: inside the trigger column they made it taller than
          the action column, and the centred row pushed the arrow and the action dropdown below
          the trigger dropdown. The anchor line is specific to this trigger; the hour applies to
          every time-based one. */}
      {TriggerConfigs[triggerType]?.isTimeBased && (
        <Grid container>
          <Grid item xs={5} className={classes.hints}>
            {triggerType === TriggerTypes.PatientEveryXWeeks && (
              <DazzedParagraph12>
                {t('pages.accountSettings.automations.editorModal.timeBasedAnchorHint')}
              </DazzedParagraph12>
            )}
            {executionHour && (
              <DazzedParagraph12>
                {t('pages.accountSettings.automations.editorModal.timeBasedExecutionHint', {
                  hour: formatExecutionHour(executionHour)
                })}
              </DazzedParagraph12>
            )}
          </Grid>
        </Grid>
      )}
    </div>
  )
}

export default RuleEditor
